/**
 * Lyd + vibrasjon ved QR-skann — så sjåføren merker bekreftelsen uten å se på skjermen.
 * Web Audio (ingen lydfiler); AudioContext må «primes» i en brukerhandling (iOS Safari). Vibrasjon støttes ikke på iPhone-Safari.
 */
export type ScanOutcome = 'ok' | 'already' | 'not_found' | 'error';

const SOUND_KEY = 'te_scan_sound';
let ctx: AudioContext | null = null;

export const isScanSoundOn = () => {
  try { return localStorage.getItem(SOUND_KEY) !== 'off'; } catch { return true; }
};

export const setScanSoundOn = (on: boolean) => {
  try { localStorage.setItem(SOUND_KEY, on ? 'on' : 'off'); } catch { /* ignorer */ }
  if (on) primeScanFeedback();
};

/** Kall fra en brukerhandling (tapp/klikk) så lyden er låst opp når skannet kommer. */
export const primeScanFeedback = () => {
  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    ctx = ctx || new AC();
    if (ctx.state === 'suspended') void ctx.resume();
  } catch { /* ignorer */ }
};

const tone = (freq: number, at: number, dur: number, type: OscillatorType = 'sine', gain = 0.18) => {
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(gain, at + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  osc.connect(g).connect(ctx.destination);
  osc.start(at);
  osc.stop(at + dur + 0.02);
};

const PATTERNS: Record<ScanOutcome, { vibrate: number[]; play: (t0: number) => void }> = {
  ok: { vibrate: [70], play: (t0) => { tone(880, t0, 0.09); tone(1320, t0 + 0.1, 0.14); } },
  already: { vibrate: [40, 60, 40], play: (t0) => { tone(660, t0, 0.12); tone(660, t0 + 0.16, 0.12); } },
  not_found: { vibrate: [200], play: (t0) => tone(220, t0, 0.28, 'square', 0.12) },
  error: { vibrate: [200], play: (t0) => tone(220, t0, 0.28, 'square', 0.12) },
};

export const scanFeedback = (outcome: ScanOutcome) => {
  const p = PATTERNS[outcome];
  try { navigator.vibrate?.(p.vibrate); } catch { /* ignorer */ }
  if (!isScanSoundOn()) return;
  try {
    primeScanFeedback();
    if (!ctx) return;
    const play = () => { try { if (ctx && ctx.state === 'running') p.play(ctx.currentTime); } catch { /* ignorer */ } };
    if (ctx.state === 'running') play();
    else ctx.resume().then(play).catch(() => { /* ignorer */ });
  } catch { /* ignorer */ }
};
